
import { supabase } from "@/integrations/supabase/client";
import { SupportTicket, TicketComment, TicketAttachment, TicketFilter } from "@/lib/types/support-tickets";
import { toast } from "@/components/ui/use-toast";
import { createLogger } from "@/lib/utils/logger";

const logger = createLogger('system', 'SupportTicketService');

// Helper to join profiles data with tickets
const addUserDetails = async (tickets: any[]): Promise<SupportTicket[]> => {
  if (!tickets.length) return [];
  
  // Get all unique user IDs
  const userIds = Array.from(new Set([
    ...tickets.map(t => t.created_by).filter(Boolean),
    ...tickets.map(t => t.assigned_to).filter(Boolean)
  ]));

  // Fetch user details
  const { data: profiles } = await supabase
    .from('profiles')
    .select('id, first_name, last_name')
    .in('id', userIds);

  const userMap = new Map();
  profiles?.forEach(profile => {
    userMap.set(profile.id, `${profile.first_name || ''} ${profile.last_name || ''}`.trim() || 'Unknown User');
  });

  // Add user names to tickets
  return tickets.map(ticket => ({
    ...ticket,
    creator_name: userMap.get(ticket.created_by) || 'Unknown User',
    assignee_name: ticket.assigned_to ? userMap.get(ticket.assigned_to) || 'Unassigned' : 'Unassigned'
  }));
};

export const getTickets = async (filters?: TicketFilter): Promise<SupportTicket[]> => {
  try {
    let query = supabase
      .from('support_tickets')
      .select('*')
      .order('created_at', { ascending: false });
    
    // Apply filters if provided
    if (filters) {
      if (filters.status && filters.status.length > 0) {
        query = query.in('status', filters.status);
      }
      
      if (filters.type && filters.type.length > 0) {
        query = query.in('ticket_type', filters.type);
      }
      
      if (filters.priority && filters.priority.length > 0) {
        query = query.in('priority', filters.priority);
      }
      
      if (filters.createdBy) {
        query = query.eq('created_by', filters.createdBy);
      }
      
      if (filters.assignedTo) {
        query = query.eq('assigned_to', filters.assignedTo);
      }
      
      if (filters.dateRange && filters.dateRange[0] && filters.dateRange[1]) {
        query = query.gte('created_at', filters.dateRange[0].toISOString())
                    .lte('created_at', filters.dateRange[1].toISOString());
      }
      
      if (filters.searchQuery) {
        query = query.or(`title.ilike.%${filters.searchQuery}%,description.ilike.%${filters.searchQuery}%`);
      }
    }
    
    const { data, error } = await query;
    
    if (error) {
      logger.error(`Error fetching tickets: ${error.message}`);
      return [];
    }
    
    return await addUserDetails(data || []);
  } catch (error: any) {
    logger.error(`Unexpected error fetching tickets: ${error.message}`);
    toast({
      title: "Error fetching tickets",
      description: "Please try again later.",
      variant: "destructive"
    });
    return [];
  }
};

export const getTicketById = async (id: string): Promise<SupportTicket | null> => {
  try {
    const { data, error } = await supabase
      .from('support_tickets')
      .select('*')
      .eq('id', id)
      .single();
    
    if (error) {
      logger.error(`Error fetching ticket ${id}: ${error.message}`);
      return null;
    }
    
    const ticketsWithUserDetails = await addUserDetails([data]);
    return ticketsWithUserDetails[0] || null;
  } catch (error: any) {
    logger.error(`Unexpected error fetching ticket ${id}: ${error.message}`);
    toast({
      title: "Error fetching ticket details",
      description: "Please try again later.",
      variant: "destructive"
    });
    return null;
  }
};

export const createTicket = async (ticket: Omit<SupportTicket, 'id' | 'created_at' | 'updated_at'>): Promise<SupportTicket | null> => {
  try {
    const { data, error } = await supabase
      .from('support_tickets')
      .insert(ticket)
      .select()
      .single();
    
    if (error) {
      logger.error(`Error creating ticket: ${error.message}`);
      toast({
        title: "Error creating ticket",
        description: error.message,
        variant: "destructive"
      });
      return null;
    }
    
    toast({
      title: "Ticket created successfully",
      description: "Your ticket has been submitted.",
    });
    
    const ticketsWithUserDetails = await addUserDetails([data]);
    return ticketsWithUserDetails[0] || null;
  } catch (error: any) {
    logger.error(`Unexpected error creating ticket: ${error.message}`);
    toast({
      title: "Error creating ticket",
      description: "Please try again later.",
      variant: "destructive"
    });
    return null;
  }
};

export const updateTicket = async (id: string, updates: Partial<SupportTicket>): Promise<SupportTicket | null> => {
  try {
    // Don't allow updating these fields directly
    const { id: _, created_at, updated_at, creator_name, assignee_name, ...validUpdates } = updates as any;
    
    const { data, error } = await supabase
      .from('support_tickets')
      .update({ ...validUpdates, updated_at: new Date().toISOString() })
      .eq('id', id)
      .select()
      .single();
    
    if (error) {
      logger.error(`Error updating ticket ${id}: ${error.message}`);
      toast({
        title: "Error updating ticket",
        description: error.message,
        variant: "destructive"
      });
      return null;
    }
    
    toast({
      title: "Ticket updated successfully",
    });
    
    const ticketsWithUserDetails = await addUserDetails([data]);
    return ticketsWithUserDetails[0] || null;
  } catch (error: any) {
    logger.error(`Unexpected error updating ticket ${id}: ${error.message}`);
    toast({
      title: "Error updating ticket",
      description: "Please try again later.",
      variant: "destructive"
    });
    return null;
  }
};

export const addComment = async (comment: Omit<TicketComment, 'id' | 'created_at'>): Promise<TicketComment | null> => {
  try {
    const { data, error } = await supabase
      .from('ticket_comments')
      .insert(comment)
      .select()
      .single();
    
    if (error) {
      logger.error(`Error adding comment: ${error.message}`);
      toast({
        title: "Error adding comment",
        description: error.message,
        variant: "destructive"
      });
      return null;
    }
    
    // Update the ticket's updated_at timestamp
    await supabase
      .from('support_tickets')
      .update({ updated_at: new Date().toISOString() })
      .eq('id', comment.ticket_id);
    
    toast({
      title: "Comment added successfully",
    });
    
    return data;
  } catch (error: any) {
    logger.error(`Unexpected error adding comment: ${error.message}`);
    toast({
      title: "Error adding comment",
      description: "Please try again later.",
      variant: "destructive"
    });
    return null;
  }
};

export const getTicketComments = async (ticketId: string): Promise<TicketComment[]> => {
  try {
    const { data: comments, error } = await supabase
      .from('ticket_comments')
      .select('*')
      .eq('ticket_id', ticketId)
      .order('created_at', { ascending: true });
    
    if (error) {
      logger.error(`Error fetching comments for ticket ${ticketId}: ${error.message}`);
      return [];
    }
    
    // Get user details
    if (comments.length) {
      const userIds = Array.from(new Set(comments.map(c => c.user_id)));
      
      const { data: profiles } = await supabase
        .from('profiles')
        .select('id, first_name, last_name')
        .in('id', userIds);
      
      const userMap = new Map();
      profiles?.forEach(profile => {
        userMap.set(profile.id, `${profile.first_name || ''} ${profile.last_name || ''}`.trim() || 'Unknown User');
      });
      
      return comments.map(comment => ({
        ...comment,
        user_name: userMap.get(comment.user_id) || 'Unknown User'
      }));
    }
    
    return comments || [];
  } catch (error: any) {
    logger.error(`Unexpected error fetching comments: ${error.message}`);
    return [];
  }
};

// Subscribe to new ticket updates
export const subscribeToNewTickets = (callback: (ticket: SupportTicket) => void) => {
  return supabase
    .channel('ticket-updates')
    .on(
      'postgres_changes',
      {
        event: 'INSERT',
        schema: 'public',
        table: 'support_tickets',
      },
      async (payload) => {
        const newTicket = payload.new as any;
        const [ticketWithUserDetails] = await addUserDetails([newTicket]);
        callback(ticketWithUserDetails);
      }
    )
    .subscribe();
};

// Subscribe to ticket status changes
export const subscribeToTicketUpdates = (callback: (ticket: SupportTicket) => void) => {
  return supabase
    .channel('ticket-status-updates')
    .on(
      'postgres_changes',
      {
        event: 'UPDATE',
        schema: 'public',
        table: 'support_tickets',
      },
      async (payload) => {
        const updatedTicket = payload.new as any;
        const [ticketWithUserDetails] = await addUserDetails([updatedTicket]);
        callback(ticketWithUserDetails);
      }
    )
    .subscribe();
};

// Subscribe to new comments
export const subscribeToTicketComments = (ticketId: string, callback: (comment: TicketComment) => void) => {
  return supabase
    .channel(`ticket-comments-${ticketId}`)
    .on(
      'postgres_changes',
      {
        event: 'INSERT',
        schema: 'public',
        table: 'ticket_comments',
        filter: `ticket_id=eq.${ticketId}`,
      },
      async (payload) => {
        const newComment = payload.new as TicketComment;
        
        // Get user name
        const { data: profile } = await supabase
          .from('profiles')
          .select('first_name, last_name')
          .eq('id', newComment.user_id)
          .single();
        
        const userName = profile 
          ? `${profile.first_name || ''} ${profile.last_name || ''}`.trim() || 'Unknown User'
          : 'Unknown User';
        
        callback({
          ...newComment,
          user_name: userName
        });
      }
    )
    .subscribe();
};
